/**
 * Country-wise timezone mapping for campaign scheduling
 * Contains major countries with their common timezones
 */

export interface CountryTimezone {
  country: string;
  code: string;
  timezones: {
    name: string;
    value: string; // IANA timezone identifier
    offset: string; // UTC offset for display
  }[];
}

export const countryTimezones: CountryTimezone[] = [
  {
    country: "United States",
    code: "US",
    timezones: [
      { name: "Eastern Time", value: "America/New_York", offset: "UTC-5" },
      { name: "Central Time", value: "America/Chicago", offset: "UTC-6" },
      { name: "Mountain Time", value: "America/Denver", offset: "UTC-7" },
      { name: "Pacific Time", value: "America/Los_Angeles", offset: "UTC-8" },
      { name: "Alaska Time", value: "America/Anchorage", offset: "UTC-9" },
      { name: "Hawaii Time", value: "Pacific/Honolulu", offset: "UTC-10" },
    ],
  },
  {
    country: "India",
    code: "IN",
    timezones: [
      { name: "India Standard Time", value: "Asia/Kolkata", offset: "UTC+5:30" },
    ],
  },
  {
    country: "United Kingdom",
    code: "GB",
    timezones: [
      { name: "Greenwich Mean Time / British Summer Time", value: "Europe/London", offset: "UTC+0/+1" },
    ],
  },
  {
    country: "Canada",
    code: "CA",
    timezones: [
      { name: "Atlantic Time", value: "America/Halifax", offset: "UTC-4" },
      { name: "Eastern Time", value: "America/Toronto", offset: "UTC-5" },
      { name: "Central Time", value: "America/Winnipeg", offset: "UTC-6" },
      { name: "Mountain Time", value: "America/Edmonton", offset: "UTC-7" },
      { name: "Pacific Time", value: "America/Vancouver", offset: "UTC-8" },
    ],
  },
  {
    country: "Australia",
    code: "AU",
    timezones: [
      { name: "Australian Eastern Time", value: "Australia/Sydney", offset: "UTC+10/+11" },
      { name: "Australian Central Time", value: "Australia/Adelaide", offset: "UTC+9:30/+10:30" },
      { name: "Australian Western Time", value: "Australia/Perth", offset: "UTC+8" },
    ],
  },
  {
    country: "Germany",
    code: "DE",
    timezones: [
      { name: "Central European Time", value: "Europe/Berlin", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "France",
    code: "FR",
    timezones: [
      { name: "Central European Time", value: "Europe/Paris", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Japan",
    code: "JP",
    timezones: [
      { name: "Japan Standard Time", value: "Asia/Tokyo", offset: "UTC+9" },
    ],
  },
  {
    country: "China",
    code: "CN",
    timezones: [
      { name: "China Standard Time", value: "Asia/Shanghai", offset: "UTC+8" },
    ],
  },
  {
    country: "Brazil",
    code: "BR",
    timezones: [
      { name: "Brasilia Time", value: "America/Sao_Paulo", offset: "UTC-3" },
      { name: "Amazon Time", value: "America/Manaus", offset: "UTC-4" },
    ],
  },
  {
    country: "Mexico",
    code: "MX",
    timezones: [
      { name: "Central Time", value: "America/Mexico_City", offset: "UTC-6" },
      { name: "Mountain Time", value: "America/Chihuahua", offset: "UTC-7" },
      { name: "Pacific Time", value: "America/Tijuana", offset: "UTC-8" },
    ],
  },
  {
    country: "Singapore",
    code: "SG",
    timezones: [
      { name: "Singapore Time", value: "Asia/Singapore", offset: "UTC+8" },
    ],
  },
  {
    country: "United Arab Emirates",
    code: "AE",
    timezones: [
      { name: "Gulf Standard Time", value: "Asia/Dubai", offset: "UTC+4" },
    ],
  },
  {
    country: "South Africa",
    code: "ZA",
    timezones: [
      { name: "South Africa Standard Time", value: "Africa/Johannesburg", offset: "UTC+2" },
    ],
  },
  {
    country: "South Korea",
    code: "KR",
    timezones: [
      { name: "Korea Standard Time", value: "Asia/Seoul", offset: "UTC+9" },
    ],
  },
  {
    country: "Russia",
    code: "RU",
    timezones: [
      { name: "Moscow Time", value: "Europe/Moscow", offset: "UTC+3" },
      { name: "Yekaterinburg Time", value: "Asia/Yekaterinburg", offset: "UTC+5" },
      { name: "Novosibirsk Time", value: "Asia/Novosibirsk", offset: "UTC+7" },
      { name: "Vladivostok Time", value: "Asia/Vladivostok", offset: "UTC+10" },
    ],
  },
  {
    country: "Indonesia",
    code: "ID",
    timezones: [
      { name: "Western Indonesia Time", value: "Asia/Jakarta", offset: "UTC+7" },
      { name: "Central Indonesia Time", value: "Asia/Makassar", offset: "UTC+8" },
      { name: "Eastern Indonesia Time", value: "Asia/Jayapura", offset: "UTC+9" },
    ],
  },
  {
    country: "Saudi Arabia",
    code: "SA",
    timezones: [
      { name: "Arabian Standard Time", value: "Asia/Riyadh", offset: "UTC+3" },
    ],
  },
  {
    country: "Philippines",
    code: "PH",
    timezones: [
      { name: "Philippine Time", value: "Asia/Manila", offset: "UTC+8" },
    ],
  },
  {
    country: "Nigeria",
    code: "NG",
    timezones: [
      { name: "West Africa Time", value: "Africa/Lagos", offset: "UTC+1" },
    ],
  },
  {
    country: "Spain",
    code: "ES",
    timezones: [
      { name: "Central European Time", value: "Europe/Madrid", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Italy",
    code: "IT",
    timezones: [
      { name: "Central European Time", value: "Europe/Rome", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Netherlands",
    code: "NL",
    timezones: [
      { name: "Central European Time", value: "Europe/Amsterdam", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Poland",
    code: "PL",
    timezones: [
      { name: "Central European Time", value: "Europe/Warsaw", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Sweden",
    code: "SE",
    timezones: [
      { name: "Central European Time", value: "Europe/Stockholm", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "New Zealand",
    code: "NZ",
    timezones: [
      { name: "New Zealand Time", value: "Pacific/Auckland", offset: "UTC+12/+13" },
    ],
  },
  {
    country: "Israel",
    code: "IL",
    timezones: [
      { name: "Israel Standard Time", value: "Asia/Jerusalem", offset: "UTC+2/+3" },
    ],
  },
  {
    country: "Thailand",
    code: "TH",
    timezones: [
      { name: "Indochina Time", value: "Asia/Bangkok", offset: "UTC+7" },
    ],
  },
  {
    country: "Malaysia",
    code: "MY",
    timezones: [
      { name: "Malaysia Time", value: "Asia/Kuala_Lumpur", offset: "UTC+8" },
    ],
  },
  {
    country: "Pakistan",
    code: "PK",
    timezones: [
      { name: "Pakistan Standard Time", value: "Asia/Karachi", offset: "UTC+5" },
    ],
  },
  {
    country: "Bangladesh",
    code: "BD",
    timezones: [
      { name: "Bangladesh Standard Time", value: "Asia/Dhaka", offset: "UTC+6" },
    ],
  },
  {
    country: "Egypt",
    code: "EG",
    timezones: [
      { name: "Eastern European Time", value: "Africa/Cairo", offset: "UTC+2" },
    ],
  },
  {
    country: "Vietnam",
    code: "VN",
    timezones: [
      { name: "Indochina Time", value: "Asia/Ho_Chi_Minh", offset: "UTC+7" },
    ],
  },
  {
    country: "Argentina",
    code: "AR",
    timezones: [
      { name: "Argentina Time", value: "America/Argentina/Buenos_Aires", offset: "UTC-3" },
    ],
  },
  {
    country: "Colombia",
    code: "CO",
    timezones: [
      { name: "Colombia Time", value: "America/Bogota", offset: "UTC-5" },
    ],
  },
  {
    country: "Chile",
    code: "CL",
    timezones: [
      { name: "Chile Time", value: "America/Santiago", offset: "UTC-4/-3" },
    ],
  },
  {
    country: "Turkey",
    code: "TR",
    timezones: [
      { name: "Turkey Time", value: "Europe/Istanbul", offset: "UTC+3" },
    ],
  },
  {
    country: "Kenya",
    code: "KE",
    timezones: [
      { name: "East Africa Time", value: "Africa/Nairobi", offset: "UTC+3" },
    ],
  },
  {
    country: "Ireland",
    code: "IE",
    timezones: [
      { name: "Irish Standard Time", value: "Europe/Dublin", offset: "UTC+0/+1" },
    ],
  },
  {
    country: "Switzerland",
    code: "CH",
    timezones: [
      { name: "Central European Time", value: "Europe/Zurich", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Austria",
    code: "AT",
    timezones: [
      { name: "Central European Time", value: "Europe/Vienna", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Belgium",
    code: "BE",
    timezones: [
      { name: "Central European Time", value: "Europe/Brussels", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Portugal",
    code: "PT",
    timezones: [
      { name: "Western European Time", value: "Europe/Lisbon", offset: "UTC+0/+1" },
    ],
  },
  {
    country: "Greece",
    code: "GR",
    timezones: [
      { name: "Eastern European Time", value: "Europe/Athens", offset: "UTC+2/+3" },
    ],
  },
  {
    country: "Norway",
    code: "NO",
    timezones: [
      { name: "Central European Time", value: "Europe/Oslo", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Denmark",
    code: "DK",
    timezones: [
      { name: "Central European Time", value: "Europe/Copenhagen", offset: "UTC+1/+2" },
    ],
  },
  {
    country: "Finland",
    code: "FI",
    timezones: [
      { name: "Eastern European Time", value: "Europe/Helsinki", offset: "UTC+2/+3" },
    ],
  },
  {
    country: "Hong Kong",
    code: "HK",
    timezones: [
      { name: "Hong Kong Time", value: "Asia/Hong_Kong", offset: "UTC+8" },
    ],
  },
  {
    country: "Taiwan",
    code: "TW",
    timezones: [
      { name: "Taipei Time", value: "Asia/Taipei", offset: "UTC+8" },
    ],
  },
].sort((a, b) => a.country.localeCompare(b.country));

// Flattened timezone list for Select dropdowns
export const flatTimezoneList = countryTimezones.flatMap((country) =>
  country.timezones.map((tz) => ({
    value: tz.value,
    label: `${country.country} - ${tz.name} (${tz.offset})`,
    country: country.country,
    code: country.code,
  }))
);

// Helper function to get current time in a specific timezone
export function getCurrentTimeInTimezone(timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(new Date());
  } catch {
    return '--:--';
  }
}

// Helper function to get formatted date-time for a specific timezone
export function getFormattedDateTimeInTimezone(date: Date, timezone: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

// Convert local date-time to UTC timestamp based on selected timezone
export function localToUTC(localDateTimeString: string, timezone: string): Date {
  // Parse the local datetime string (format: "YYYY-MM-DDTHH:mm")
  const [datePart, timePart] = localDateTimeString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  // Create a formatter that will interpret the time in the selected timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Create a date object in the local timezone first
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

  // Get the timezone offset for the selected timezone
  const parts = formatter.formatToParts(localDate);
  const getPart = (type: string) => parts.find(p => p.type === type)?.value || '';

  // Create a date string that JavaScript can parse with timezone
  const tzDate = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);

  // Calculate the offset difference
  const localOffset = localDate.getTimezoneOffset();
  const tzFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'shortOffset',
  });

  // Use a more reliable method: create the date as if it's in the target timezone
  const targetDate = new Date(
    Date.UTC(year, month - 1, day, hours, minutes, 0, 0) - getTimezoneOffsetMs(timezone, new Date(year, month - 1, day, hours, minutes))
  );

  return targetDate;
}

// Get timezone offset in milliseconds
function getTimezoneOffsetMs(timezone: string, date: Date): number {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  return (tzDate.getTime() - utcDate.getTime());
}
